import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Loader2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Initializing...');

  useEffect(() => {
    // Use ref to prevent double-processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        setStatus('Extracting session data...');
        console.log('[AuthCallback] Starting OAuth callback processing');
        console.log('[AuthCallback] Location hash:', location.hash);

        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);

        if (!sessionIdMatch) {
          console.error('[AuthCallback] No session_id found in hash');
          throw new Error('No session ID found in callback URL');
        }

        const sessionId = sessionIdMatch[1];
        console.log('[AuthCallback] Session ID extracted:', sessionId.substring(0, 20) + '...');

        setStatus('Exchanging session token...');
        console.log('[AuthCallback] Calling /api/auth/session');

        // Exchange session_id for session_token
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          {
            withCredentials: true,
            timeout: 30000 // 30 second timeout
          }
        );

        console.log('[AuthCallback] Response received:', {
          status: response.status,
          hasSessionToken: !!response.data?.session_token,
          hasUserId: !!response.data?.user_id,
          userName: response.data?.name
        });

        // CRITICAL: Store session_token in localStorage for mobile browsers
        // Mobile browsers often block cross-origin cookies
        if (response.data?.session_token) {
          console.log('[AuthCallback] Storing session_token in localStorage');
          localStorage.setItem('session_token', response.data.session_token);
        } else {
          console.warn('[AuthCallback] No session_token in response!');
        }

        setStatus('Setting up user session...');
        setUser(response.data);

        // Clear the hash and prevent backtracking to the oauth login error
        console.log('[AuthCallback] Authentication successful, redirecting to dashboard');
        window.history.replaceState(null, '', '/');
        // Push dashboard so that hitting back takes the user to '/'
        navigate('/dashboard', { replace: false });

      } catch (err) {
        console.error('[AuthCallback] Error during OAuth callback:', err);
        console.error('[AuthCallback] Error details:', {
          message: err.message,
          code: err.code,
          response: err.response?.data,
          status: err.response?.status
        });

        let errorMessage = 'Authentication failed';

        if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
          errorMessage = 'Network error - please check your connection and try again';
        } else if (err.response?.status === 401) {
          errorMessage = 'Session expired - please try logging in again';
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        setTimeout(() => navigate('/', { replace: true }), 3000);
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-500 text-lg font-semibold mb-2">Authentication Failed</p>
          <p className="text-white/70 mb-4">{error}</p>
          <p className="text-white/50 text-sm">Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#00F0FF] animate-spin mx-auto mb-4" />
        <p className="text-white/70 font-heading mb-2">Authenticating...</p>
        <p className="text-white/50 text-sm">{status}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
